import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { useDynamicBranding } from "../root/useDynamicBranding";
import { useCRM } from "../context/CRMContext";
import { useLogout } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import Header from "./Header";
import { GlobalSearchProvider } from "../search/GlobalSearchContext";
import { GlobalSearchDialog } from "../search/GlobalSearchDialog";
import { GlobalSearchTrigger } from "../search/GlobalSearchTrigger";
import { CrmImpersonationBanner } from "./CrmImpersonationBanner";
import { SidebarCollapseProvider, useSidebarCollapse } from "./SidebarContext";
import { LegalFooter } from "../legal/LegalFooter";

const TopBar = () => {
  const { isCollapsed, toggleSidebar } = useSidebarCollapse();

  return (
    <header className="h-16 shrink-0 z-20 border-b border-border/60 bg-card/60 backdrop-blur-md px-4 sm:px-6 flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors cursor-pointer"
          title={
            isCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"
          }
          aria-label={
            isCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"
          }
        >
          {isCollapsed ? (
            <PanelLeftOpen className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </Button>
      </div>

      <div className="w-full max-w-md lg:max-w-xl flex items-center justify-center">
        <GlobalSearchTrigger variant="topbar" />
      </div>

      <div className="flex-1 min-w-0 flex items-center justify-end" />
    </header>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  useDynamicBranding();
  const { currentCrm, isLoading, isSuperadmin, refreshCrms } = useCRM();
  const logout = useLogout();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-muted-foreground animate-pulse">
            Carregando ambiente...
          </p>
        </div>
      </div>
    );
  }

  if (!isLoading && !currentCrm && !isSuperadmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Acesso Indisponível
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Seu CRM foi desativado ou excluído pelo administrador da plataforma.
            Você não possui acesso a nenhum ambiente no momento.
          </p>
          <div className="pt-2 flex flex-col gap-2">
            <Button
              variant="default"
              onClick={() => refreshCrms()}
              className="w-full text-xs cursor-pointer"
            >
              Tentar novamente
            </Button>
            <Button
              variant="outline"
              onClick={() => logout()}
              className="gap-2 w-full text-xs cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
              Sair da Conta
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <SidebarCollapseProvider>
      <GlobalSearchProvider>
        <div className="flex h-screen w-screen overflow-hidden bg-background text-foreground">
          {/* WCAG 2.1 AA 2.4.1 - Skip Link para navegação por teclado */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary focus:text-primary-foreground focus:text-xs focus:font-semibold focus:shadow-lg"
          >
            Pular para o conteúdo principal
          </a>

          {/* Sidebar Lateral Esquerda */}
          <Header />

          {/* Conteúdo Principal da Aplicação */}
          <div className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">
            <CrmImpersonationBanner />

            {/* Barra Superior com Pesquisa Geral Centralizada e Toggle de Sidebar */}
            <TopBar />

            <main
              className="flex-1 overflow-y-auto p-6 max-w-full flex flex-col justify-between gap-8"
              id="main-content"
            >
              <div className="flex-1">
                <ErrorBoundary FallbackComponent={Error}>
                  <Suspense
                    fallback={<Skeleton className="h-12 w-12 rounded-full" />}
                  >
                    {children}
                  </Suspense>
                </ErrorBoundary>
              </div>
              <LegalFooter compact />
            </main>
          </div>

          <Notification />
          <GlobalSearchDialog />
        </div>
      </GlobalSearchProvider>
    </SidebarCollapseProvider>
  );
};
